'use client'

import * as React from 'react'
import { Slider } from '@/components/ui/slider'
import { cn } from '@/lib/utils'
import { useTranslations } from 'next-intl'

interface PricingSliderProps {
  tiers: Array<{
    id: string
    name: string
    memberLimit: number | null
  }>
  selectedTierId: string | null
  onSelectTier: (id: string) => void
  className?: string
}

export function PricingSlider({
  tiers,
  selectedTierId,
  onSelectTier,
  className,
}: PricingSliderProps) {
  const t = useTranslations('landing')
  // Map tiers to steps 0..N-1
  const maxIndex = Math.max(1, tiers.length - 1)

  // Find current index based on selectedTierId
  const currentIndex = React.useMemo(() => {
    const idx = tiers.findIndex((t) => t.id === selectedTierId)
    return idx === -1 ? 0 : idx
  }, [tiers, selectedTierId])

  const handleValueChange = (values: number[]) => {
    const index = values[0]
    if (tiers[index]) {
      onSelectTier(tiers[index].id)
    }
  }

  return (
    <div className={cn('w-full', className)}>
      <div className="relative pt-6 pb-2 px-1">
        <Slider
          defaultValue={[currentIndex]}
          value={[currentIndex]}
          max={maxIndex}
          step={1}
          onValueChange={handleValueChange}
          className="cursor-pointer w-full relative z-10"
        />

        {/* Ticks/Labels */}
        <div className="relative mt-4 w-full h-12 select-none pointer-events-none">
          {tiers.map((tier, i) => (
            <div
              key={tier.id}
              className="absolute top-0 flex flex-col items-center cursor-pointer group pointer-events-auto"
              style={{
                left: `calc(0.625rem + ${(i / maxIndex) * 100}% - ${(i / maxIndex) * 1.25}rem)`,
                transform: 'translateX(-50%)',
              }}
              onClick={() => onSelectTier(tier.id)}
            >
              <div
                className={cn(
                  'h-2 w-0.5 mb-2 transition-colors rounded-full',
                  i === currentIndex ? 'bg-primary' : 'bg-border group-hover:bg-primary/50'
                )}
              />
              <span
                className={cn(
                  'text-xs font-medium transition-colors text-center whitespace-nowrap',
                  i === currentIndex
                    ? 'text-primary'
                    : 'text-muted-foreground group-hover:text-foreground'
                )}
              >
                {tier.name}
              </span>
              <span className="text-[10px] text-muted-foreground hidden sm:block whitespace-nowrap">
                {tier.memberLimit === null
                  ? t('pricing.calculator.unlimited')
                  : tier.memberLimit >= 1000
                    ? `${tier.memberLimit / 1000}k`
                    : tier.memberLimit}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
